import { AxiosError } from 'axios'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { ContainerHistoryQueryResult, ContainerUpdateDto } from 'modules/integration/src/app/api'
import { containerHistoryService } from 'modules/integration/src/services'
import { ContainerItemStore } from '../../../rt-stores/container/ContainerItemStore'
import { containerService } from '../../../services'
import { EventDifferenceModel } from '../utils/container-history-details.model'
import { handleContainerHistoryEvent } from '../utils/container-history-details.utils'

export interface EnhancedContainerUpdateDto extends ContainerUpdateDto {
  eventDifferences: EventDifferenceModel[]
  userName?: string
}

export interface ContainerHistoryAdditionalData {
  userNames?: { [key: string]: string } | null
  carrierVisitNames?: { [key: string]: string } | null
  gangNames?: { [key: string]: string } | null
  shiftNames?: { [key: string]: string } | null
}

export interface OperationsContainerHistoryAdditionalDataDto {
  gangs: { [key: string]: string }
  shifts: { [key: string]: string }
}

export type FetchOpsAdditionalDataFn = (
  gangIds: number[],
  shiftIds: number[],
) => Promise<OperationsContainerHistoryAdditionalDataDto>

export class ContainerJourneyHistoryViewStore {
  containerId?: number
  containerHistoryDto?: ContainerHistoryQueryResult
  containerHistoryAdditionalData?: ContainerHistoryAdditionalData

  constructor(
    private readonly containerItemStore: ContainerItemStore,
    private readonly fetchOpsAdditionalData?: FetchOpsAdditionalDataFn,
  ) {
    makeObservable(this, {
      containerId: observable,
      containerHistoryDto: observable,

      container: computed,
      sortedEvents: computed,

      setContainerId: action,
    })
  }

  get container() {
    return this.containerId ? this.containerItemStore.elements[this.containerId] : undefined
  }

  get sortedEvents() {
    const sortedEvents = (_.orderBy(
      this.containerHistoryDto?.Events,
      event => event.Event!.EventTime,
      'desc',
    ) || []) as EnhancedContainerUpdateDto[]

    const lookupSources = {
      carrierVisitNames: this.containerHistoryAdditionalData?.carrierVisitNames || null,
      containerNumbers: null, //TODO: not implemented
      customerNames: null, //TODO: not implemented
      userNames: this.containerHistoryAdditionalData?.userNames || null,
      gangNames: this.containerHistoryAdditionalData?.gangNames || null,
      shiftNames: this.containerHistoryAdditionalData?.shiftNames || null,
    }

    sortedEvents.forEach(event => {
      if (event.Event?.UserId) {
        event.userName = this.containerHistoryAdditionalData?.userNames?.[event.Event?.UserId]
      }

      event.eventDifferences = handleContainerHistoryEvent(event, lookupSources)
    })

    return sortedEvents
  }

  setContainerId = (containerId?: number) => (this.containerId = containerId)

  fetch = async (containerId: number, turnoverId?: string) => {
    this.setContainerId(containerId)

    await this.containerItemStore.fetchById(containerId)

    const containerHistory: ContainerHistoryQueryResult | undefined = await containerHistoryService
      .getContainerHistory(containerId, turnoverId)
      .catch((error: AxiosError) =>
        error.response?.status === 404 ? undefined : Promise.reject(error),
      )

    const additionalInfoIds = containerHistory?.Events?.reduce(
      (acc, event) => {
        const userId = event.Event?.UserId
        if (userId) acc.userIds.push(userId)

        const getDifferenceValues = (path: string): string[] => {
          return (
            event.Differences?.filter(diff => diff.Path === path).reduce<string[]>((acc, diff) => {
              if (diff.PreviousValue && diff.PreviousValue !== 'null') acc.push(diff.PreviousValue)
              if (diff.NewValue && diff.NewValue !== 'null') acc.push(diff.NewValue)
              return acc
            }, []) || []
          )
        }

        const trimQuotes = (value: string) => value.replace(/^"(.*)"$/, '$1')

        const userIdsFromDiff = getDifferenceValues('.UserId').map(userId => trimQuotes(userId))
        const carrierVisitIds = getDifferenceValues('.CarrierVisitId').map(visitId =>
          Number(visitId),
        )
        const gangIds = getDifferenceValues('.GangId').map(gangId => Number(gangId))
        const shiftIds = getDifferenceValues('.ShiftId').map(shiftId => Number(shiftId))

        if (userIdsFromDiff) acc.userIds.push(...userIdsFromDiff)
        if (carrierVisitIds) acc.carrierVisitIds.push(...carrierVisitIds)

        if (gangIds) acc.gangIds.push(...gangIds)
        if (shiftIds) acc.shiftIds.push(...shiftIds)

        return acc
      },
      {
        userIds: [] as string[],
        carrierVisitIds: [] as number[],
        gangIds: [] as number[],
        shiftIds: [] as number[],
      },
    )

    const uniqueUserIds = _.uniq(additionalInfoIds?.userIds || [])
    const uniqueCarrierVisitIds = _.uniq(additionalInfoIds?.carrierVisitIds || []).filter(
      id => !isNaN(id),
    )
    const uniqueGangIds = _.uniq(additionalInfoIds?.gangIds || []).filter(id => !isNaN(id))
    const uniqueShiftds = _.uniq(additionalInfoIds?.shiftIds || []).filter(id => !isNaN(id))

    const opsAdditionalData = this.fetchOpsAdditionalData
      ? await this.fetchOpsAdditionalData(uniqueGangIds, uniqueShiftds)
      : undefined

    const planningAdditionalData = await containerService.getContainerHistoryAdditionalData(
      uniqueUserIds,
      uniqueCarrierVisitIds,
    )

    const allAdditionalData = {
      userNames: planningAdditionalData.userNames,
      carrierVisitNames: planningAdditionalData.carrierVisitNames,
      gangNames: opsAdditionalData?.gangs,
      shiftNames: opsAdditionalData?.shifts,
    }

    runInAction(() => {
      this.containerHistoryDto = containerHistory ?? undefined
      this.containerHistoryAdditionalData = allAdditionalData
    })
  }
}
